<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import {
    previewArrivalTimes,
    type IArrivalPreviewSettings,
    type IArrivalPreviewShift,
    type IArrivalPreviewTime,
  } from '@/domain/reservations/reservation-preview';

  export interface IPreviewShift extends IArrivalPreviewShift {
    key: string | number;
    name: string | null;
  }

  const props = withDefaults(
    defineProps<{
      shifts: IPreviewShift[];
      settings: IArrivalPreviewSettings;
      /** The strip reflects unsaved General settings. */
      settingsDirty?: boolean;
      /** Show at most this many chips per shift before a "+N" chip. */
      limit?: number;
    }>(),
    { settingsDirty: false, limit: 0 }
  );
  const proxy = getCurrentInstance()!.proxy;

  interface IPreviewRow {
    key: string | number;
    name: string | null;
    fixed: boolean;
    times: IArrivalPreviewTime[];
    hidden: number;
  }

  const rows = computed<IPreviewRow[]>(() =>
    props.shifts.map((shift) => {
      const times = previewArrivalTimes(shift, props.settings);
      const visible = props.limit > 0 ? times.slice(0, props.limit) : times;
      return {
        key: shift.key,
        name: shift.name,
        fixed: shift.arrivalTimes !== null,
        times: visible,
        hidden: times.length - visible.length,
      };
    })
  );
  const allTimes = computed(() => rows.value.flatMap((row) => row.times));
  const hasNextDay = computed(() => allTimes.value.some((t) => t.nextDay));
  const isPartial = (time: IArrivalPreviewTime) =>
    !time.fitsAll && time.served.length > 0;
  const hasPartial = computed(() => allTimes.value.some(isPartial));
  const hasUnreachable = computed(() =>
    allTimes.value.some((t) => !t.served.length)
  );
  function chipClass(time: IArrivalPreviewTime) {
    return { partial: isPartial(time), unreachable: !time.served.length };
  }
  /** "hasta N" for a small-party prefix, "algunos grupos" for any other subset. */
  function partialNote(time: IArrivalPreviewTime): string {
    if (!isPartial(time)) return '';
    return time.fitsUpToParty != null
      ? String(proxy.$t('reservations.preview.partialShort', { count: time.fitsUpToParty }))
      : String(proxy.$t('reservations.preview.partialSome'));
  }
</script>

<template>
  <div class="slots-preview" role="group" :aria-label="$t('reservations.preview.title')">
    <div class="preview-heading">
      <span>{{ $t('reservations.preview.title') }}</span>
      <span v-if="settingsDirty" class="draft-note">
        {{ $t('reservations.preview.usesDraft') }}
      </span>
    </div>
    <p v-if="!rows.length" class="preview-empty">
      {{ $t('reservations.preview.noShifts') }}
    </p>
    <div v-for="row in rows" :key="row.key" class="preview-shift">
      <div v-if="row.name || row.fixed" class="shift-heading">
        <span v-if="row.name" class="shift-name">{{ row.name }}</span>
        <PosUiBadge
          v-if="row.fixed"
          :label="$t('reservations.preview.fixed')"
          color="neutral"
          variant="outline"
          size="xs"
        />
      </div>
      <p v-if="!row.times.length" class="preview-none" role="status">
        {{ $t('reservations.preview.none') }}
      </p>
      <ul v-else class="chip-strip">
        <li
          v-for="time in row.times"
          :key="time.offsetMin"
          class="time-chip"
          :class="chipClass(time)"
        >
          <span class="chip-time">{{ time.time }}</span
          ><sup v-if="time.nextDay" class="chip-next">+1</sup
          ><span v-if="partialNote(time)" class="chip-note">{{
            partialNote(time)
          }}</span
          ><span v-if="time.maxCovers != null" class="chip-note">{{
            $t('reservations.preview.cap', { count: time.maxCovers })
          }}</span>
        </li>
        <li v-if="row.hidden > 0" class="time-chip more">
          {{ $t('reservations.preview.more', { count: row.hidden }) }}
        </li>
      </ul>
    </div>
    <ul v-if="hasNextDay || hasPartial || hasUnreachable" class="legend">
      <li v-if="hasNextDay">{{ $t('reservations.preview.nextDay') }}</li>
      <li v-if="hasPartial">{{ $t('reservations.preview.partial') }}</li>
      <li v-if="hasUnreachable">{{ $t('reservations.preview.unreachable') }}</li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
  .slots-preview {
    @apply tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-bg-canvas tw-p-3;
    .preview-heading {
      @apply tw-flex tw-flex-wrap tw-items-baseline tw-justify-between tw-gap-x-3 tw-text-xs tw-font-medium tw-text-neutral-600;
    }
    .draft-note {
      @apply tw-font-normal tw-text-amber-700;
    }
    .preview-empty,
    .preview-none {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .preview-none {
      @apply tw-text-amber-700;
    }
    .preview-shift {
      @apply tw-flex tw-flex-col tw-gap-1.5;
    }
    .shift-heading {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
    .shift-name {
      @apply tw-text-xs tw-font-semibold tw-text-neutral-800;
    }
    .chip-strip {
      @apply tw-m-0 tw-flex tw-list-none tw-flex-wrap tw-gap-1.5 tw-p-0;
    }
    .time-chip {
      @apply tw-inline-flex tw-h-7 tw-items-center tw-gap-1 tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-2 tw-text-xs tw-font-medium tw-tabular-nums tw-text-neutral-800;
      &.partial {
        @apply tw-border-dashed tw-border-neutral-400 tw-text-neutral-600;
      }
      &.unreachable {
        @apply tw-border-transparent tw-bg-neutral-100 tw-text-neutral-400 tw-line-through;
      }
      &.more {
        @apply tw-border-transparent tw-bg-neutral-100 tw-text-neutral-500;
      }
    }
    .chip-next {
      @apply tw-text-[10px] tw-text-neutral-500;
    }
    .chip-note {
      @apply tw-text-[11px] tw-font-normal tw-text-neutral-500;
    }
    .legend {
      @apply tw-m-0 tw-list-none tw-p-0 tw-text-[11px] tw-leading-relaxed tw-text-neutral-500;
    }
  }
</style>
